import { CheckIcon, ChevronDownIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useState, type FC } from "react";
import { Input } from "../ui/input";
import useDebounce from "@/hooks/useDebounce";
import Text from "../Text";
import { useTranslation } from "react-i18next";
import {
  autoCompleteRestaurantState,
  searchForAutoCompleteRestaurant,
} from "@/store/autoCompleteRestaurantState";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";

type Props = {
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
  isRestricted: boolean;
};

const SearchRestaurantName: FC<Props> = ({
  onChange,
  defaultValue,
  isRestricted,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [value, setValue] = useState<string[]>(defaultValue ?? []);

  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = autoCompleteRestaurantState();

  useEffect(() => {
    if (debouncedSearch.length > 2) {
      searchForAutoCompleteRestaurant({ search });
    }
  }, [debouncedSearch]);

  let restaurantsAutoComplet: string[] = [];

  if (data) {
    restaurantsAutoComplet = [
      ...new Set(data.map((restaurant) => restaurant.name)),
    ];
  }

  restaurantsAutoComplet = restaurantsAutoComplet.filter(
    (restaurant) => !value.find((v) => v === restaurant),
  );

  useEffect(() => {
    onChange?.(value);
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className="w-[100%] justify-between bg-[white]"
          style={{
            border: "1px solid #B3B9C4",
          }}
          onClick={() => {
            if (isRestricted) {
              setActionTriggerNeedCore(true);
            }
          }}
        >
          <Text className="truncate">
            {value.length > 0 ? value.join(", ") : t("select_restaurants")}
          </Text>

          {isRestricted ? (
            <Lock
              width={24}
              height={24}
              color="#535862"
              className="ml-2 h-4 w-4 shrink-0"
            />
          ) : (
            <ChevronDownIcon
              width={24}
              height={24}
              color="#758195"
              className="ml-2 h-4 w-4 shrink-0"
              style={{
                transition: "transform 0.3s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      {!isRestricted && (
        <PopoverContent className="w-[355px] p-[12px]">
          <Input
            placeholder={t("research_restaurants")}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <div className="flex flex-col gap-[8px] my-[8px]">
            {value.map((citie, index) => (
              <button
                key={`value-${citie}-${index}`}
                onClick={() =>
                  setValue((prevState) => {
                    const copyPrevState = [...prevState];
                    const citieIndexToRemove = copyPrevState.indexOf(citie);

                    if (citieIndexToRemove !== -1)
                      copyPrevState.splice(citieIndexToRemove, 1);

                    return copyPrevState;
                  })
                }
                className="flex items-center gap-[8px] px-[12px] py-[8px] hover:bg-[#F1F2F4]"
              >
                <CheckIcon height={16} width={16} />
                <Text fontSize={16} fontWeight={400}>
                  {citie}
                </Text>
              </button>
            ))}
          </div>
          {value.length > 0 && restaurantsAutoComplet.length > 0 && <hr />}
          {isLoading && (
            <div className="mt-[12px]">
              <Text>{t("searching_process")}</Text>
            </div>
          )}
          <div className="flex flex-col gap-[8px] mt-[8px]">
            {restaurantsAutoComplet.map((restaurant, index) => (
              <button
                key={`auto-complete-${restaurant}-${index}`}
                onClick={() => {
                  setSearch("");
                  setValue((prevState) => [...prevState, restaurant]);
                }}
                className="flex px-[12px] py-[8px] hover:bg-[#F1F2F4]"
              >
                <Text fontSize={16} fontWeight={400}>
                  {restaurant}
                </Text>
              </button>
            ))}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default SearchRestaurantName;
